import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Volume2, 
  Mic, 
  ClipboardList, 
  Trophy,
  TrendingUp,
  Award,
  ArrowRight,
  Film,
  PenLine,
  Sparkles,
  Zap,
  Target,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, attemptsRes] = await Promise.all([
        api.get('/statistics?period=monthly'),
        api.get('/attempts')
      ]);
      setStats(statsRes.data);
      setAttempts(attemptsRes.data.slice(0, 5)); // Last 5 attempts
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const moduleCards = [
    { 
      name: 'Text to Speech', 
      type: 'tts',
      icon: Volume2, 
      iconColor: 'text-cyan-400',
      bgColor: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
      borderColor: 'border-cyan-500/30 hover:border-cyan-400',
      glowColor: 'hover:shadow-cyan-500/25',
      description: 'Read text passages aloud',
      href: '/student/tts'
    },
    { 
      name: 'Speech to Text', 
      type: 'stt',
      icon: Mic, 
      iconColor: 'text-emerald-400',
      bgColor: 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20',
      borderColor: 'border-emerald-500/30 hover:border-emerald-400',
      glowColor: 'hover:shadow-emerald-500/25',
      description: 'Listen and type what you hear',
      href: '/student/stt'
    },
    { 
      name: 'MCQ Quiz', 
      type: 'mcq',
      icon: ClipboardList, 
      iconColor: 'text-amber-400',
      bgColor: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
      borderColor: 'border-amber-500/30 hover:border-amber-400',
      glowColor: 'hover:shadow-amber-500/25',
      description: 'Test your knowledge',
      href: '/student/mcq'
    },
    { 
      name: 'Fill the Blanks', 
      type: 'ftb',
      icon: PenLine, 
      iconColor: 'text-orange-400',
      bgColor: 'bg-gradient-to-br from-orange-500/20 to-red-500/20',
      borderColor: 'border-orange-500/30 hover:border-orange-400',
      glowColor: 'hover:shadow-orange-500/25',
      description: 'Complete sentences with missing words',
      href: '/student/ftb',
      isNew: true
    },
    { 
      name: 'Cinematic MCQ', 
      type: 'vmcq',
      icon: Film, 
      iconColor: 'text-purple-400',
      bgColor: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-500/30 hover:border-purple-400',
      glowColor: 'hover:shadow-purple-500/25',
      description: 'Watch video, answer questions',
      href: '/student/vmcq'
    },
  ];

  const getModuleStats = (type) => {
    const stat = stats.find(s => s.module_type === type);
    return {
      attempts: stat?.total_attempts || 0,
      bestScore: Math.round(stat?.best_score || 0)
    };
  };

  // Get module icon component
  const getModuleIcon = (type) => {
    const icons = { tts: Volume2, stt: Mic, mcq: ClipboardList, vmcq: Film, ftb: PenLine };
    return icons[type] || ClipboardList;
  };

  // Get module color classes
  const getModuleColor = (type) => {
    const colors = {
      tts: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400' },
      stt: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
      mcq: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
      vmcq: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
      ftb: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' }
    };
    return colors[type] || colors.mcq;
  };

  // Get module display name
  const getModuleName = (type) => {
    const names = { tts: 'Text to Speech', stt: 'Speech to Text', mcq: 'MCQ Quiz', vmcq: 'Cinematic MCQ', ftb: 'Fill the Blanks' };
    return names[type] || type.toUpperCase();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,white)]" />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-2">
                Welcome, {user?.name}!
                <Sparkles className="h-6 w-6 text-yellow-300 animate-pulse" />
              </h1>
              <p className="text-white/80 mt-1">
                Ready to practice your English skills?
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
              <Target className="h-5 w-5 text-yellow-300" />
              <div className="text-sm">
                <p className="text-white/70">Today's Goal</p>
                <p className="font-semibold">Complete 3 Tests</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lab Modules - Premium Grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-5 w-5 text-amber-500" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Lab Modules
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 stagger-children">
            {moduleCards.map((module, idx) => {
              const Icon = module.icon;
              const moduleStats = getModuleStats(module.type);
              return (
                <Link key={idx} to={module.href} className="block">
                  <Card className={`module-card h-full group cursor-pointer relative overflow-hidden border-2 ${module.borderColor} ${module.glowColor} hover:shadow-xl transition-all duration-300`}>
                    {module.isNew && (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full animate-pulse flex items-center gap-1">
                          <Star className="h-3 w-3" /> NEW
                        </span>
                      </div>
                    )}
                    <div className={`absolute inset-0 ${module.bgColor} opacity-50`} />
                    <CardContent className="p-6 relative z-10">
                      <div className={`inline-flex p-3 rounded-xl ${module.bgColor} mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                        <Icon className={`h-6 w-6 ${module.iconColor}`} />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                        {module.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        {module.description}
                      </p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Attempts: {moduleStats.attempts}</span>
                        <span className={`font-bold px-2 py-0.5 rounded-full ${
                          moduleStats.bestScore >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
                          moduleStats.bestScore >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' :
                          'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          Best: {moduleStats.bestScore}%
                        </span>
                      </div>
                      <div className="mt-4 flex items-center text-indigo-600 dark:text-indigo-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Start Practice <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Quick Stats & Leaderboard Link */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Attempts - Premium Card */}
          <Card className="overflow-hidden border-2 border-slate-200 dark:border-slate-700">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {attempts.length > 0 ? (
                <div className="space-y-3">
                  {attempts.map((attempt, idx) => {
                    const Icon = getModuleIcon(attempt.module_type);
                    const colors = getModuleColor(attempt.module_type);
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${colors.bg}`}>
                            <Icon className={`h-4 w-4 ${colors.text}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {getModuleName(attempt.module_type)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(attempt.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className={`text-lg font-bold px-3 py-1 rounded-full ${
                          attempt.score >= 80 ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400' :
                          attempt.score >= 50 ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400' :
                          'bg-rose-100 text-rose-600 dark:bg-rose-900/50 dark:text-rose-400'
                        }`}>
                          {Math.round(attempt.score)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No attempts yet. Start practicing!</p>
              )}
            </CardContent>
          </Card>

          {/* Leaderboard Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <Award className="h-16 w-16 mx-auto text-amber-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-300 mb-4">
                  See how you rank against your classmates!
                </p>
                <Link to="/student/leaderboard">
                  <Button className="bg-amber-500 hover:bg-amber-600" data-testid="view-leaderboard-btn">
                    <Trophy className="h-4 w-4 mr-2" />
                    View Leaderboard
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
